import { Button, Flex, Table, Text } from '@workwhile/ui'
import { RefreshCcw } from 'lucide-react'
import * as moment from 'moment-timezone'
import React, { useState } from 'react'

import api from 'api'
import CollapsibleSection from 'components/Shared/CollapsibleSection'
import { User } from 'components/Shared/common_defs'
import StatusSetter from 'components/Shared/StatusSetter'
import { type BackgroundCheckStatus } from 'types/backgroundCheck'
import { UserDrugScreenings } from './UserDrugScreenings'
import { UserEducationAndEmployment } from './UserEducationEmployment'
import { UserPremiumPlusFederal } from './UserPremiumFederal'

interface Props {
  user: User
  refreshData: () => void
  defaultOpen: boolean
}

const PROVIDER_TEXT = 'Yardstik'
const STATUS_OPTIONS = [
  { label: 'Manually clear', value: 'manually_clear' },
  { label: 'Final adverse', value: 'final_adverse' },
  { label: 'NULL (clear status)', value: null },
]
const MVR_STATUS_OPTIONS = STATUS_OPTIONS.filter(
  (option) => option.value !== 'manually_clear'
)
const DATE_TIME_SUBMITTED_FORMAT = 'YYYY-MM-DD h:mma z'

const REPORT_TYPES = [
  'basic',
  'mvr',
  'reorder_mvr', // Not actually a report type
  'basic_plus_federal',
  'premium',
  'premium_plus_federal',
  'eight_panel_drug_screening',
  'ten_panel_minus_thc_drug_screening',
  'reorder_ten_panel_minus_thc_drug_screening', // Not actually a report type
  'ten_panel_drug_screening',
  'four_panel_minus_thc_drug_screening',
  'reorder_four_panel_minus_thc_drug_screening', // Not actually a report type
  'ontrac_eight_panel_drug_screening',
  'ontrac_nine_panel_drug_screening',
  'education_verification_high_school',
  'education_and_employment_verification',
] as const

type ReportType = (typeof REPORT_TYPES)[number]

const REPORT_TYPES_TEXT: Record<ReportType, string> = {
  basic: 'Basic',
  mvr: 'MVR',
  reorder_mvr: '(Reorder) MVR',
  basic_plus_federal: 'Basic + Federal',
  premium: 'Premium',
  premium_plus_federal: 'Premium + Federal',
  eight_panel_drug_screening: '8 panel drug screening',
  ten_panel_minus_thc_drug_screening: '10 panel minus THC drug screening',
  reorder_ten_panel_minus_thc_drug_screening:
    '(Reorder) 10 panel minus THC drug screening',
  ten_panel_drug_screening: '10 panel drug screening',
  four_panel_minus_thc_drug_screening: '4 panel minus THC drug screening',
  reorder_four_panel_minus_thc_drug_screening:
    '(Reorder) 4 panel minus THC drug screening',
  ontrac_eight_panel_drug_screening: 'OnTrac 8 panel drug screening',
  ontrac_nine_panel_drug_screening: 'OnTrac 9 panel drug screening',
  education_verification_high_school: 'Education verification: High school',
  education_and_employment_verification:
    'Education and employment verification',
}
const REPORT_TYPES_OPTIONS = Object.entries(REPORT_TYPES_TEXT).map(
  ([value, label]) => {
    return { label, value }
  }
)

function renderRowKey(value, row) {
  const refresh = row.original.refresh
  if (refresh === undefined) {
    return <Text color="lightText">{value}</Text>
  }

  return (
    <Flex align="center">
      <Text color="lightText">{value}</Text>
      {refresh.visible ? (
        <Button
          kind="small"
          onClick={refresh.handleClick}
          style={{ padding: 4 }}
          variant="text"
          disabled={refresh.fetching}
        >
          <RefreshCcw size={14} />
        </Button>
      ) : null}
    </Flex>
  )
}

const keyValueColumns = [
  {
    accessorKey: 'key',
    cell: ({ getValue, row }) => {
      return renderRowKey(getValue(), row)
    },
  },
  {
    accessorKey: 'value',
    cell: ({ getValue, row }) => {
      if (row.original.link) {
        return (
          <a target="_blank" rel="noreferrer" href={row.original.link}>
            {getValue()}
          </a>
        )
      }
      return <Text>{getValue()}</Text>
    },
  },
]

function orderReportRequestBody(reportType: ReportType) {
  if (reportType === 'reorder_mvr') {
    return { report_type: 'mvr', reorder: true }
  }

  if (reportType === 'reorder_ten_panel_minus_thc_drug_screening') {
    return { report_type: 'ten_panel_minus_thc_drug_screening', reorder: true }
  }

  if (reportType === 'reorder_four_panel_minus_thc_drug_screening') {
    return { report_type: 'four_panel_minus_thc_drug_screening', reorder: true }
  }

  return { report_type: reportType, reorder: false }
}

function updateStatusRequestBody(
  statusType: 'criminal' | 'mvr',
  newStatus: BackgroundCheckStatus | null,
  reason: string
) {
  return {
    status_type: statusType,
    status: newStatus,
    reason,
  }
}

const makeReportUrl = (reportId: string) =>
  `https://app.yardstik.com/report/${encodeURIComponent(reportId)}`

async function refreshReport(reportId: string): Promise<void> {
  await api.post(`/admin/yardstik/refresh`, { report_id: reportId })
}

const UserYardstikVerification = (props: Props) => {
  const { user, refreshData } = props
  const timezone = user.address?.timezone || moment.tz.guess(true)

  const [newCriminalStatus, setNewCriminalStatus] =
    useState<BackgroundCheckStatus | null>('manually_clear') // input defaults to manually_clear
  const [submittingCriminalStatus, setSubmittingCriminalStatus] =
    useState(false)

  const [newMvrStatus, setNewMvrStatus] =
    useState<BackgroundCheckStatus | null>('final_adverse')
  const [submittingMvrStatus, setSubmittingMvrStatus] = useState(false)

  const [reportType, setReportType] = useState<ReportType>('basic') // input defaults to basic
  const [submittingOrderReport, setSubmittingOrderReport] = useState(false)

  const [criminalStatusRefreshFetching, setCriminalStatusRefreshFetching] =
    useState(false)
  const [mvrStatusRefreshFetching, setMVRStatusRefreshFetching] =
    useState(false)

  const providerStatusMetadata = user.yardstikStatus

  const criminalStatus = providerStatusMetadata?.criminalStatus
  const criminalSubmitted = providerStatusMetadata?.criminalSubmitted
  const mvrStatus = providerStatusMetadata?.mvrStatus
  const mvrSubmitted = providerStatusMetadata?.mvrSubmitted

  const candidateId = providerStatusMetadata?.candidateId
  const hasCandidate = Boolean(candidateId)
  const setStatusDisabled = !hasCandidate

  const criminalReportID = providerStatusMetadata?.criminalReportId
  const mvrReportID = providerStatusMetadata?.mvrReportId

  const shouldLinkCriminal =
    Boolean(criminalStatus) && Boolean(criminalReportID)
  const shouldLinkMvr = Boolean(mvrStatus) && Boolean(mvrReportID)
  const hasAnyStatus = Boolean(criminalStatus) || Boolean(mvrStatus)
  const defaultOpen = props.defaultOpen && hasAnyStatus

  const handleClickCriminalRefresh = async () => {
    if (!criminalReportID) {
      return
    }

    setCriminalStatusRefreshFetching(true)
    try {
      await refreshReport(criminalReportID)
    } catch (error) {
      console.error('ERROR OCCURED REFRESHING CRIMINAL STATUS: ', error)
    }

    setCriminalStatusRefreshFetching(false)
    refreshData()
  }

  const handleClickMVRRefresh = async () => {
    if (!mvrReportID) {
      return
    }

    setMVRStatusRefreshFetching(true)
    try {
      await refreshReport(mvrReportID)
    } catch (error) {
      console.error('ERROR OCCURED REFRESHING MVR STATUS: ', error)
    }

    setMVRStatusRefreshFetching(false)
    refreshData()
  }

  const refreshReportAndData = async (reportID: string) => {
    try {
      await refreshReport(reportID)
    } finally {
      refreshData()
    }
  }

  const refreshCriminalVisible = Boolean(criminalReportID)
  const refreshMVRVisible = Boolean(mvrReportID)

  const data = React.useMemo(
    () => [
      {
        key: 'criminal_status',
        value: criminalStatus || 'N/A',
        link: shouldLinkCriminal ? makeReportUrl(criminalReportID ?? '') : null,
        refresh: {
          visible: refreshCriminalVisible,
          handleClick: handleClickCriminalRefresh,
          fetching: criminalStatusRefreshFetching,
        },
      },
      {
        key: 'criminal_submitted_at',
        value: criminalSubmitted
          ? moment
              .tz(criminalSubmitted, timezone)
              .format(DATE_TIME_SUBMITTED_FORMAT)
          : 'N/A',
      },
      {
        key: 'mvr_status',
        value: mvrStatus || 'N/A',
        link: shouldLinkMvr ? makeReportUrl(mvrReportID ?? '') : null,
        refresh: {
          visible: refreshMVRVisible,
          handleClick: handleClickMVRRefresh,
          fetching: mvrStatusRefreshFetching,
        },
      },
      {
        key: 'mvr_submitted_at',
        value: mvrSubmitted
          ? moment.tz(mvrSubmitted, timezone).format(DATE_TIME_SUBMITTED_FORMAT)
          : 'N/A',
      },
    ],
    []
  )

  const submitCriminalStatusChange = () => {
    setSubmittingCriminalStatus(true)
    const r = window.prompt(
      `You are about to set USER(${user.id}) ${PROVIDER_TEXT} Criminal status to: ${newCriminalStatus}. To execute this action, please press OK.`
    )
    if (r != null) {
      api
        .post(
          `/admin/yardstik/status/${user.id}`,
          updateStatusRequestBody('criminal', newCriminalStatus, r)
        )
        .then((_response) => {
          alert(`${PROVIDER_TEXT} Criminal status is now ${newCriminalStatus}`)
          setSubmittingCriminalStatus(false)
          refreshData()
        })
        .catch((error) => {
          console.error('ERROR OCCURED: ', error)
          setSubmittingCriminalStatus(false)
        })
    } else {
      alert('Action Cancelled!')
      setSubmittingCriminalStatus(false)
    }
    return
  }

  const submitMvrStatusChange = () => {
    setSubmittingMvrStatus(true)
    const r = window.prompt(
      `You are about to set USER(${user.id}) ${PROVIDER_TEXT} MVR status to: ${newMvrStatus}. To execute this action, please press OK.`
    )
    if (r != null) {
      api
        .post(
          `/admin/yardstik/status/${user.id}`,
          updateStatusRequestBody('mvr', newMvrStatus, r)
        )
        .then((_response) => {
          alert(`${PROVIDER_TEXT} MVR status is now ${newMvrStatus}`)
          setSubmittingMvrStatus(false)
          refreshData()
        })
        .catch((error) => {
          console.error('ERROR OCCURED: ', error)
          setSubmittingMvrStatus(false)
        })
    } else {
      alert('Action Cancelled!')
      setSubmittingMvrStatus(false)
    }
    return
  }

  const submitOrderReport = () => {
    setSubmittingOrderReport(true)
    const r = window.prompt(
      `You are about to order a ${PROVIDER_TEXT} report for USER(${user.id}): ${reportType}. To execute this action, please press OK.`
    )
    if (r != null) {
      api
        .post(
          `/admin/yardstik/order_report/${user.id}`,
          orderReportRequestBody(reportType)
        )
        .then((_response) => {
          alert(`${PROVIDER_TEXT} report ordered: ${reportType}`)
          setSubmittingOrderReport(false)
          refreshData()
        })
        .catch((error) => {
          console.error('ERROR OCCURED: ', error)
          setSubmittingOrderReport(false)
        })
    } else {
      alert('Action Cancelled!')
      setSubmittingOrderReport(false)
    }
    return
  }

  return (
    <CollapsibleSection
      title={`${PROVIDER_TEXT} Details`}
      defaultOpen={defaultOpen}
    >
      <Table variant="borderless" data={data} columns={keyValueColumns} />
      <StatusSetter
        label={`Set ${PROVIDER_TEXT} Criminal Status`}
        disabled={setStatusDisabled}
        name="newCriminalStatus"
        value={newCriminalStatus}
        handleChange={(status) => setNewCriminalStatus(status)}
        isLoading={submittingCriminalStatus}
        submitStatusChange={submitCriminalStatusChange}
        options={STATUS_OPTIONS}
        buttonType="confirm"
      />
      <StatusSetter
        label={`Set ${PROVIDER_TEXT} MVR Status`}
        disabled={setStatusDisabled}
        name="newMvrStatus"
        value={newMvrStatus}
        handleChange={(status) => setNewMvrStatus(status)}
        isLoading={submittingMvrStatus}
        submitStatusChange={submitMvrStatusChange}
        options={MVR_STATUS_OPTIONS}
        buttonType="confirm"
      />
      <StatusSetter
        label={`Order ${PROVIDER_TEXT} Report`}
        name="orderReport"
        value={reportType}
        handleChange={(selected) => setReportType(selected)}
        isLoading={submittingOrderReport}
        submitStatusChange={submitOrderReport}
        options={REPORT_TYPES_OPTIONS}
        buttonType="confirm"
        buttonText="Order Report"
      />
      <UserDrugScreenings
        drugScreenings={providerStatusMetadata?.drugScreenings}
        makeReportUrl={makeReportUrl}
        userTimeZone={timezone}
        refreshReport={refreshReportAndData}
      />
      <div style={{ marginTop: '24px' }}>
        <UserPremiumPlusFederal
          premiumPlusFederalReports={
            providerStatusMetadata?.premiumPlusFederalReports
          }
          makeReportUrl={makeReportUrl}
          refreshReport={refreshReportAndData}
        />
      </div>
      <div style={{ marginTop: '24px' }}>
        <UserEducationAndEmployment
          educationAndEmploymentReports={
            providerStatusMetadata?.educationAndEmploymentReports
          }
          makeReportUrl={makeReportUrl}
          refreshReport={refreshReportAndData}
        />
      </div>
    </CollapsibleSection>
  )
}

export default UserYardstikVerification
